/**
 * Date/time helpers — display layer formats UTC ISO timestamps in
 * Europe/Moscow per architecture §3.2. Domain logic stays UTC.
 */
import { formatDistanceToNow, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ru as ruLocale, enUS } from "date-fns/locale";

export const APP_TZ = "Europe/Moscow";

const LOCALES = { ru: ruLocale, en: enUS } as const;
export type SupportedLocale = keyof typeof LOCALES;

function resolveLocale(locale?: string): (typeof LOCALES)[SupportedLocale] {
  const key = (locale ?? "ru").slice(0, 2) as SupportedLocale;
  return LOCALES[key] ?? LOCALES.ru;
}

/** Format an ISO datetime as "DD.MM.YYYY HH:mm" in Moscow time (ru) by default. */
export function formatDateTime(iso: string, locale?: string): string {
  return formatInTimeZone(parseISO(iso), APP_TZ, "dd.MM.yyyy HH:mm", {
    locale: resolveLocale(locale),
  });
}

/** Format an ISO date (YYYY-MM-DD) as a localized short date — no time. */
export function formatDate(isoDate: string, locale?: string): string {
  return formatInTimeZone(parseISO(`${isoDate}T00:00:00Z`), APP_TZ, "dd.MM.yyyy", {
    locale: resolveLocale(locale),
  });
}

/** Format an ISO date (YYYY-MM-DD) as a localized long date — "17 мая 2026" / "May 17, 2026". */
export function formatLongDate(isoDate: string, locale?: string): string {
  const lang = (locale ?? "ru").slice(0, 2);
  const pattern = lang === "en" ? "MMMM d, yyyy" : "d MMMM yyyy";
  return formatInTimeZone(parseISO(`${isoDate}T00:00:00Z`), APP_TZ, pattern, {
    locale: resolveLocale(locale),
  });
}

/** Format an ISO date (YYYY-MM-DD) as "weekday, DD.MM" — "пятница, 05.06" / "Friday, 05.06". */
export function formatWeekdayDate(isoDate: string, locale?: string): string {
  return formatInTimeZone(parseISO(`${isoDate}T00:00:00Z`), APP_TZ, "EEEE, dd.MM", {
    locale: resolveLocale(locale),
  });
}

/** Format an ISO datetime as "HH:mm" in Moscow time. */
export function formatTime(iso: string): string {
  return formatInTimeZone(parseISO(iso), APP_TZ, "HH:mm");
}

/**
 * Format an ISO datetime as relative time — "3 дня назад" / "3 days ago".
 * A same-day timestamp collapses to "сегодня, 09:14" instead: within the
 * current day "0 hours ago" says less than the clock time does.
 *
 * "Same day" is the Moscow calendar day, not the last 24 hours — 23:00
 * yesterday reads as "около 9 часов назад", not as today.
 *
 * `todayLabel` comes in from the caller's i18n: helpers here take words, they
 * don't reach for `t()`.
 */
export function formatRelativeDateTime(
  iso: string,
  opts: { todayLabel: string; locale?: string },
): string {
  const date = parseISO(iso);
  const moscowDay = (d: Date): string => formatInTimeZone(d, APP_TZ, "yyyy-MM-dd");
  if (moscowDay(date) === moscowDay(new Date())) {
    return `${opts.todayLabel}, ${formatTime(iso)}`;
  }
  return formatDistanceToNow(date, { addSuffix: true, locale: resolveLocale(opts.locale) });
}
